import React from "react";
import { useTranslation } from "react-i18next";
import { PlannerData } from "../../hooks/useGenericPlanner";
import { usePlanningConfiguration } from "../../hooks/usePlanningConfiguration";
import Datatable from "../../../../../components/Common/dataTable";
import "./GenericPlanner.css";
import { getSubjectCodeFromkey } from "utils/schoolUtils";

interface GenericPlannerGridProps {
  role: string;
  data: PlannerData[];
}

const toMinutes = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const normalize = (ts: string) => ts.slice(0, 5);


const GenericPlannerGrid: React.FC<GenericPlannerGridProps> = ({ role, data }) => {
  const { t } = useTranslation();
  const { config, loading } = usePlanningConfiguration();
  if (loading) return <p>{t("loading")}</p>;

  const { days, timeSlots } = config;

  const dataSource = days?.map((day) => {
    const dayData = data.filter((d) => String(d.day) === day.value);
    const record: any = { key: day.value, day: t(day.label) };

    for (let i = 0; i < timeSlots.length; i++) {
      const slot = timeSlots[i];
      const slotStart = normalize(slot.start);
      const slotEnd = normalize(slot.end);
      const slotStartMin = toMinutes(slotStart);
      const slotEndMin = toMinutes(slotEnd);

      const entriesInSlot = dayData.filter((d) => {
        const entryStartMin = toMinutes(d.startTime);
        const entryEndMin = toMinutes(d.endTime);
        return entryStartMin < slotEndMin && entryEndMin > slotStartMin;
      });

      if (entriesInSlot.length === 0) {
        record[slot.start] = { type: "empty" };
        continue;
      }

      if (entriesInSlot.length === 1) {
        const entry = entriesInSlot[0];
        const entryStartMin = toMinutes(entry.startTime);
        const entryEndMin = toMinutes(entry.endTime);

        if (entryStartMin <= slotStartMin && entryEndMin >= slotEndMin) {
          record[slot.start] = { type: "multi", entry, span: 1 };
        } else {
          record[slot.start] = { type: "partial", entries: [entry] };
        }
        continue;
      }

      record[slot.start] = { type: "partial", entries: entriesInSlot };
    }

    return record;
  });

  const renderContent = (entry: PlannerData) => (
    <div className="cell-content" style={{ background: entry.subjectColor }}>
      {role === "teacher" ? (
        <>
          <div className="cell-item">
            <i className="ti ti-group cell-icon" />
            <span className="cell-text">{entry.className}</span>
          </div>
          <div className="cell-item">
            <i className="ti ti-book cell-icon" />
            <span className="cell-text">{getSubjectCodeFromkey(entry?.subjectCode)}</span>
          </div>
          <div className="cell-item">
            <i className="ti ti-clock cell-icon" />
            <span className="cell-text">{entry.startTime} – {entry.endTime}</span>
          </div>
        </>
      ) : (
        <>
          <div className="cell-item">
            <i className="ti ti-book cell-icon" />
            <span className="cell-text">{getSubjectCodeFromkey(entry?.subjectCode)}</span>
          </div>
          <div className="cell-item">
            <i className="ti ti-user cell-icon" />
            <span className="cell-text">{entry.teacherName}</span>
          </div>
          <div className="cell-item">
            <i className="ti ti-clock cell-icon" />
            <span className="cell-text">{entry.startTime} – {entry.endTime}</span>
          </div>
        </>
      )}
    </div>
  );

  const columns = [
    {
      title: t("day"),
      dataIndex: "day",
      key: "day",
      fixed: "left" as const,
      width: 120,
      className: "day-column",

    },
    ...(timeSlots?.map((slot) => {
      const slotStart = normalize(slot.start);
      const slotEnd = normalize(slot.end);
      const slotStartMin = toMinutes(slotStart);
      const slotEndMin = toMinutes(slotEnd);
      const halfPoint = slotStartMin + (slotEndMin - slotStartMin) / 2;

      return {
        title: slot.label || `${slotStart} – ${slotEnd}`,
        dataIndex: slot.start,
        key: slot.start,
        width: 150,
        className: "time-column",

        render: (cell: any) => {
          if (!cell) return <div className="noScheduleCell">-</div>;

          switch (cell.type) {
            case "skip":
              return { children: null, props: { colSpan: 0 } };

            case "multi":
              return {
                children: renderContent(cell.entry),
                props: { colSpan: cell.span },
              };

            case "partial": {
              const firstHalf = cell.entries.find((e: PlannerData) => {
                const start = toMinutes(e.startTime);
                const end = toMinutes(e.endTime);
                return start < halfPoint && end > slotStartMin;
              });

              const secondHalf = cell.entries.find((e: PlannerData) => {
                const start = toMinutes(e.startTime);
                const end = toMinutes(e.endTime);
                return start < slotEndMin && end > halfPoint;
              });

              const onlyOne = (!!firstHalf) !== (!!secondHalf);
              const containerClass = `dualSlotCell ${onlyOne ? (firstHalf ? "one-half-left" : "one-half-right") : ""
                }`;

              return (
                <div className={containerClass}>
                  {firstHalf && (
                    <div className="halfSlot halfSlot--left">
                      {renderContent(firstHalf)}
                    </div>
                  )}
                  {secondHalf && (
                    <div className="halfSlot halfSlot--right">
                      {renderContent(secondHalf)}
                    </div>
                  )}
                </div>
              );
            }

            default:
              return <div className="noScheduleCell">-</div>;
          }
        },
      };
    }) || []),
  ];

  return (
    <div className="planner-datatable-wrapper">
      <Datatable
        columns={columns}
        dataSource={dataSource}
        Selection={false}
        bordered={true}
        pagination={false}
      />
    </div>
  );
};

export default GenericPlannerGrid;
